import { IWorkflowFilterFieldOption, IWorkflowFilterTypeOption } from '@query'
import { FilterCondition, FilterNodeConfig, RelativeDatetimeValue } from './types'

export const DEFAULT_FILTER_CONFIG: FilterNodeConfig = {
  filter_type: 'document',
  match: 'all',
  conditions: []
}

export const DEFAULT_RELATIVE_DATETIME: RelativeDatetimeValue = {
  kind: 'relative',
  amount: 7,
  unit: 'days'
}

export const isRelativeDatetimeValue = (
  value: FilterCondition['value']
): value is RelativeDatetimeValue =>
  typeof value === 'object' && value !== null && 'kind' in value && value.kind === 'relative'

const parseRelativeDatetimeValue = (
  value: RelativeDatetimeValue | Record<string, unknown>
): RelativeDatetimeValue => {
  const amount =
    typeof value.amount === 'number' && Number.isFinite(value.amount)
      ? Math.max(1, Math.floor(value.amount))
      : DEFAULT_RELATIVE_DATETIME.amount

  const unit = typeof value.unit === 'string' ? value.unit : DEFAULT_RELATIVE_DATETIME.unit

  return {
    kind: 'relative',
    amount,
    unit
  }
}

export const getFilterTypeSpec = (
  catalog: IWorkflowFilterTypeOption[],
  filterType: string
): IWorkflowFilterTypeOption | undefined => catalog.find(item => item.value === filterType)

export const getFieldSpec = (
  typeSpec: IWorkflowFilterTypeOption | undefined,
  field: string
): IWorkflowFilterFieldOption | undefined => typeSpec?.fields.find(item => item.value === field)

export const defaultValueForField = (
  fieldSpec: IWorkflowFilterFieldOption
): FilterCondition['value'] => {
  if (fieldSpec.type === 'boolean') return false
  if (fieldSpec.type === 'text') return ''
  if (fieldSpec.type === 'account') return null
  if (fieldSpec.type === 'contact') return []
  if (fieldSpec.type === 'amount') return null
  return DEFAULT_RELATIVE_DATETIME
}

export const createDefaultCondition = (
  typeSpec: IWorkflowFilterTypeOption
): FilterCondition | null => {
  const fieldSpec = typeSpec.fields[0]
  if (!fieldSpec) return null

  const operator = fieldSpec.operators[0]?.value
  if (!operator) return null

  return {
    field: fieldSpec.value,
    operator,
    value: defaultValueForField(fieldSpec)
  }
}

export const normalizeConditionForField = (
  condition: FilterCondition,
  fieldSpec: IWorkflowFilterFieldOption
): FilterCondition => {
  const operatorAllowed = fieldSpec.operators.some(item => item.value === condition.operator)
  const operator = operatorAllowed
    ? condition.operator
    : fieldSpec.operators[0]?.value ?? condition.operator

  if (fieldSpec.type === 'boolean') {
    return {
      field: fieldSpec.value,
      operator,
      value: condition.value === true || condition.value === 'true'
    }
  }

  if (fieldSpec.type === 'text') {
    return {
      field: fieldSpec.value,
      operator,
      value: typeof condition.value === 'string' ? condition.value : ''
    }
  }

  if (fieldSpec.type === 'account') {
    const accountId =
      typeof condition.value === 'number' &&
      Number.isInteger(condition.value) &&
      condition.value > 0
        ? condition.value
        : null
    return {
      field: fieldSpec.value,
      operator,
      value: accountId
    }
  }

  if (fieldSpec.type === 'contact') {
    const contactIds = Array.isArray(condition.value)
      ? condition.value.filter(
          (item): item is number => typeof item === 'number' && Number.isInteger(item) && item > 0
        )
      : []
    return {
      field: fieldSpec.value,
      operator,
      value: contactIds
    }
  }

  if (fieldSpec.type === 'amount') {
    const amount =
      typeof condition.value === 'number' && Number.isFinite(condition.value)
        ? condition.value
        : null
    return {
      field: fieldSpec.value,
      operator,
      value: amount
    }
  }

  if (isRelativeDatetimeValue(condition.value)) {
    return {
      field: fieldSpec.value,
      operator,
      value: parseRelativeDatetimeValue(condition.value)
    }
  }

  const stringValue = typeof condition.value === 'string' ? condition.value : ''
  return {
    field: fieldSpec.value,
    operator,
    value: stringValue
  }
}

export const normalizeConditionsForFilterType = (
  catalog: IWorkflowFilterTypeOption[],
  filterType: string,
  conditions: FilterCondition[]
): FilterCondition[] => {
  const typeSpec = getFilterTypeSpec(catalog, filterType)
  if (!typeSpec) return []

  return conditions
    .map(condition => {
      const fieldSpec = getFieldSpec(typeSpec, condition.field) ?? typeSpec.fields[0]
      if (!fieldSpec) return null
      return normalizeConditionForField(condition, fieldSpec)
    })
    .filter((condition): condition is FilterCondition => condition !== null)
}

export const toDatetimeLocalValue = (isoValue: string): string => {
  if (!isoValue) return ''

  const date = new Date(isoValue)
  if (Number.isNaN(date.getTime())) return ''

  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`
}

export const fromDatetimeLocalValue = (localValue: string): string => {
  if (!localValue) return ''

  const date = new Date(localValue)
  if (Number.isNaN(date.getTime())) return ''

  return date.toISOString()
}
